// Import any services you need
import { databaseService } from './DatabaseService.js';

/**
 * A collection of expander functions that enhance DatabaseQuery objects
 * Each function takes a DatabaseQuery instance and returns the same instance
 */

/**
 * Expand wildcard terms to include all possible tokens matched on them.
 * @param {DatabaseQuery} query - The query to expand
 * @returns {DatabaseQuery} - The same query instance
 */
export const withWildcards = (query) => {
  const compiled = query.compile();
  
  compiled.wildcardTerms.forEach(token => {
    if (token.startsWith('+') && token.endsWith('+')) {
      const tokens = databaseService.getTokensBySubstring(token.slice(1, -1));
      query.addExpansion(token, tokens);
    } else if (token.startsWith('+')) {
      const tokens = databaseService.getTokensBySuffix(token.slice(1));
      query.addExpansion(token, tokens);
    } else if (token.endsWith('+')) {
      const tokens = databaseService.getTokensByPrefix(token.slice(0, -1));
      query.addExpansion(token, tokens);
    } else {
        // Invalid wildcard term. Nothing to do. Validation will catch this problem.
    }
  });
  
  return query;
};

/**
 * Adds grammatical variants to the query
 * @param {DatabaseQuery} query - The query to expand
 * @returns {DatabaseQuery} - The same query instance
 */
export const withGrammaticalVariants = (query) => {
  const compiled = query.compile();
  
  compiled.simpleTokens.forEach(token => {
    const variants = generateAllVariants(token);
    query.addExpansion(
        token,
        // Only add variants that exist in the database. Many won't exist.
        variants.filter(variant => databaseService.hasToken(variant))
    );
  });
  
  return query;
};

/**
 * Generate all possible variants of a token
 * @param {string} word - The word to generate variants for
 * @returns {string[]} An array of all possible variants
 */
function generateAllVariants(word) {
  const variants = new Set([word]);

  //@todo: Add a loop to add all variants for each hyphenated variant
  
  // Special case for co-ord. It's never coord...
  if (word.includes('coordinat')) {
    word = word.replace(/coordinat/g, 'co-ordinat');
    variants.add(word);
  }

  // Define all possible suffixes
  const suffixes = [
    "'", "'s", "s", "s'", "es", "ed", "ing", "ly",
    "er", "est", "ers", "ful", "al", "ally", "ness",
    "ies", "ion", "ied", "ist", "ism", "ity",
    "ment", "tion", "tions", "ments", "ship", "ships"
  ];

  // Add variants for both full word and wordMinus1
  const wordMinus1 = word.slice(0, -1);
  [word, wordMinus1].forEach(baseWord => {
    suffixes.forEach(suffix => {
      variants.add(`${baseWord}${suffix}`);
    });
  });

  // Special cases based on endings
  if (word.endsWith('e')) {
    variants.add(`${word}d`);
  }
  if (word.endsWith('te')) {
    variants.add(`${word.slice(0, -2)}ion`);
    variants.add(`${word.slice(0, -2)}ions`);
  }

  // Words with standalone dash, e.g. super-
  variants.add(`${word}-`);

  // If there are hyphens in the word, try removing them. 
  if (word.includes('-')) {
    variants.add(word.replace(/-/g, ''));

  } else {
    // Some words can be hyphenated or not.
    const nonwordPrefixes = [
      'anglo', // anglo-saxon
      'anti',
      'bel', // bel-marduk
      'beth', // beth-marion etc
      'bi',
      'co',
      'counter',
      'cro',
      'dis',
      'dyaus', // dyaus-zeus
      'en', // en-gannim
      'ex', // ex-chief etc
      'free', // free-will, freewill
      'fu', // fu-tze
      'mid',
      'non',
      'oft',
      'post',
      'pre',
      'proto',
      'quasi',
      're',
      'religio',
      'rig', // rig-veda
      'semi',
      'shang', // shang-ti
      'sonta', // sonta-an and sonta-en
      'sub',
      'super',
      'superco', // superco-ordinators
      'tom', // tom-toms
      'un'
    ];
    
    // For each prefix that matches the start of the word, add a hyphenated variant
    nonwordPrefixes.forEach(nonwordPrefix => {
      if (word.startsWith(nonwordPrefix) && word.length > nonwordPrefix.length) {
        // Get the rest of the word after the prefix
        const remainder = word.slice(nonwordPrefix.length);
        if (remainder.length > 0) {
          // Add the hyphenated variant
          variants.add(`${nonwordPrefix}-${remainder}`);
        }
      }
    });
  }

  // @todo: If hyphens in the word, try "exact match" substituting hyphens with spaces.

  // Special case for righteousness'... the only possessive of this kind in the book.
  if (word === 'righteousness') {
    variants.add('righteousness\'');
  }

  return [...variants];
}